import React, {useCallback, useEffect, useRef, useState} from 'react';
import {CommandBar, ICommandBarItemProps, Spinner, SpinnerSize, TextField} from '@fluentui/react';
import {IStorefrontLink} from "../../models/Storefront";
import {debounce} from "lodash";
import ToggleField from "../Common/ToggleField";
import {
    useDeleteStorefrontHeaderLinkMutation,
    useGetStorefrontHeaderLinksQuery,
    useSaveStorefrontHeaderLinkMutation,
    useSaveStorefrontHeaderLinksMutation
} from "../../store/endpoints/StorefrontLinkEndpoints";

interface StorefrontHeaderLinksProps {
    isGlobal?: boolean;
    storefront: string;
    saving?: boolean;
    onSaveEvent: (saving: boolean) => void;
}

const StorefrontHeaderLinks: React.FC<StorefrontHeaderLinksProps> = (props) => {
    
    const [effectiveLinks, setEffectiveLinks ] = useState<IStorefrontLink[]>([]);

    const links = useGetStorefrontHeaderLinksQuery({storefrontId: props.storefront ?? ''}, {skip: !props.storefront});

    const [setLink] = useSaveStorefrontHeaderLinkMutation();
    const [setLinks] = useSaveStorefrontHeaderLinksMutation();
    const [deleteLink] = useDeleteStorefrontHeaderLinkMutation();
    
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    useEffect(() => {
        console.log('hit the links update hook!', links.data);
        if (links.data?.items) {
            const linkItems = links.data.items;
            setEffectiveLinks(() => [...linkItems]);
        }
    }, [links.data]);
    
    const [dragOver, setDragOver] = React.useState(false);
    const handleDragOverStart = () => setDragOver(true);
    const handleDragOverEnd = () => setDragOver(false);
    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        event.dataTransfer.setData('text', event.currentTarget.id);
    }
    const enableDropping = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }
    const handleDrop = (event: React.DragEvent<HTMLDivElement>, currentSection: number) => {
        const id = event.dataTransfer.getData('text')?.replace(`link_header_`, '');
        const sourceIndex = parseInt(id ?? '-1');
        if (links.data?.items && sourceIndex !== -1 && currentSection !== -1) {
            const initialLinks = [...links.data.items];
            const [removed] = initialLinks.splice(sourceIndex, 1); // Remove the dragged column
            initialLinks.splice(currentSection, 0, removed); // Insert it at the current column's indexorder

            if(props.storefront && initialLinks){
                const updatedBanners = initialLinks.map((banner, index) => ({
                    ...banner,
                    sortOrder: index + 1
                }));
                setLinks({storefront: props.storefront ?? "", links: updatedBanners});
            }
        }
        setDragOver(false);
    }

    const debouncedUpdateLinks = useCallback(
        debounce((val:IStorefrontLink[] | undefined) => {
            if(val) {
                setLinks({links: val, storefront: props.storefront});
            }
        }, 2000, {leading: false}),
        [props.storefront]
    );
    

    const headerCommands: ICommandBarItemProps[] = [
        {
            key: 'addCommand',
            text: 'Add Link',
            title: 'Add Link',
            disabled: props.saving,
            iconProps: {iconName: 'Add'},
            onClick: () => {
                const commandHeader = [...(links.data?.items ?? [])];
                const newLink = {text: '', url: '', sortOrder: commandHeader.length + 1, storefront: props.storefront, type: 'header', disabled: true, shared: props.isGlobal == true}

                setLink({link: newLink, storefront: props.storefront}).unwrap().then(() => {
                    scrollToBottom()
                });
            }
        }
    ]

    const getHeaderCommands = (idx: number): ICommandBarItemProps[] => {
        return [
            {
                key: 'enableDisable',
                text: 'Published',
                title: 'Publish Link',
                disabled: props.saving,
                onRender: () => <ToggleField inline={true} checked={effectiveLinks?.[idx].disabled != true} label={'Published'} onChange={(_, v) => {
                    if (effectiveLinks) {
                        const b = [...effectiveLinks];
                        b[idx] = {...b[idx], disabled: v === false};
                        props.onSaveEvent(true)
                        setLinks({links: b, storefront: props.storefront}).unwrap().then(() => {props.onSaveEvent(false)});
                    }
                }} />,
            },
            {
                key: 'moveUp',
                text: 'Move Up',
                title: 'Move Up',
                iconProps: {iconName: 'ChevronUp'},
                disabled: idx === 0 || props.saving,
                onClick: () => {
                    if (effectiveLinks) {
                        const b = [...effectiveLinks];
                        const [movedBanner] = b.splice(idx, 1);
                        b.splice(idx - 1, 0, movedBanner);
                        props.onSaveEvent(true);
                        setLinks({links: b, storefront: props.storefront}).unwrap().then(() => {props.onSaveEvent(false)});
                    }
                }
            },
            {
                key: 'moveDown',
                text: 'Move Down',
                title: 'Move Down',
                iconProps: {iconName: 'ChevronDown'},
                disabled: (effectiveLinks ? idx === effectiveLinks.length - 1 : true) || props.saving,
                onClick: () => {
                    if (effectiveLinks) {
                        const b = [...effectiveLinks];
                        const [movedLink] = b.splice(idx, 1);
                        b.splice(idx + 1, 0, movedLink);
                        props.onSaveEvent(true);
                        setLinks({links: b, storefront: props.storefront}).unwrap().then(() => {props.onSaveEvent(false)});
                    }
                }
            },
            {
                key: 'delete',
                text: 'Delete',
                title: 'Delete Link',
                disabled: props.saving,
                iconProps: {iconName: 'Delete'},
                onClick: () => {
                    const linksToDelete = effectiveLinks?.[idx];
                    if (linksToDelete && linksToDelete.id) {
                        props.onSaveEvent(true);
                        deleteLink({storefront: props.storefront, id: linksToDelete.id}).unwrap().then(() => {
                            props.onSaveEvent(false);
                        })
                    }
                }
            }
        ]
    }
    
    const updateLink = (index: number, link: IStorefrontLink) => {
        const links = [...(effectiveLinks ?? [])];
        links[index] = link
        setEffectiveLinks(() => [...links])
        debouncedUpdateLinks([...links])
    }
    
    return (
        <div style={{width: '90%', minWidth: '300px'}}>
            <div>
                {(links.isFetching || links.isLoading) ? <Spinner size={SpinnerSize.large} label={'Loading Header Links...'}/> :
                    <React.Fragment>
                        <CommandBar items={headerCommands} farItems={[]}/>
                        {(effectiveLinks ?? []).map((link, idx) => (
                            <div
                                id={`link-header-${idx}`}
                                draggable
                                key={idx}
                                onDragOver={enableDropping}
                                onDrop={(ev) => handleDrop(ev, idx)}
                                onDragEnter={handleDragOverStart}
                                onDragLeave={handleDragOverEnd}
                                style={dragOver ? {width: '100%', height: '100%', background: 'rgb(207, 220, 181)'} : {}}
                            >
                                <div id={`link_header_${idx}`} className={'storefront-section'} draggable onDragStart={handleDragStart}>
                                    <CommandBar items={getHeaderCommands(idx)} farItems={[]}/>
                                    <TextField
                                        label='Text'
                                        value={link.text ?? ''}
                                        onChange={(_, v) => {
                                            updateLink(idx, { ...link, text: v ?? '' });
                                        }}
                                    />
                                    <TextField
                                        label='URL'
                                        value={link.url ?? ''}
                                        onChange={(_, v) => {
                                            updateLink(idx, { ...link, url: v ?? '' });
                                        }}
                                    />
                                    <ToggleField
                                        inline={true}
                                        label={'Is Section Header'}
                                        checked={link.isSeparator ?? false}
                                        onChange={(_, v) => {
                                            updateLink(idx, { ...link, isSeparator: v ?? false });
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                        <div ref={bottomRef} />
                        {(links.isLoading || links.isFetching || !links.data?.items || links.data.items.length === 0) ? <></> : 
                        <CommandBar items={headerCommands} farItems={[]}/>}
                    </React.Fragment>
            }
            </div>
        </div>
    );
};

export default StorefrontHeaderLinks;
